'use client';

import { type Dispatch, type SetStateAction } from 'react';
import { Minus, Plus } from 'lucide-react';
import { useToast } from '@/components/ui/Toast';
import type { ExcursionAsset } from '@/components/booking/UnifiedBookingModal';
import type { ExcursionWizardState } from '@/components/booking/excursion/ExcursionWizard';

/* ── Props ──────────────────────────────────────────────────────────────────── */

interface StepParticipantsProps {
  asset: ExcursionAsset;
  state: ExcursionWizardState;
  setState: Dispatch<SetStateAction<ExcursionWizardState>>;
}

/* ── Helpers ────────────────────────────────────────────────────────────────── */

function applyMarkup(price: number, markupRate: number): number {
  return Math.round(price * (1 + markupRate / 100));
}

/* ── Component ──────────────────────────────────────────────────────────────── */

export default function StepParticipants({ asset, state, setState }: StepParticipantsProps) {
  const toast = useToast();

  const markupRate = asset.markupRate ?? 0;
  const isTimedMode =
    asset.pricingMode === 'tiered' || asset.pricingMode === 'exclusive';

  // Clamp: min(remaining, maxCapacity)
  // TODO: min_guests non è disponibile nell'asset attualmente — il minimo è fisso a 1
  const max = Math.min(
    state.slotRemaining ?? asset.maxCapacity ?? 99,
    asset.maxCapacity ?? 99,
  );

  /* ── Tiered data ─────────────────────────────────────────────────────────── */
  const nonExclusiveTiers = (asset.pricingTiers ?? []).filter((t) => !t.exclusive);
  const hasExclusive = isTimedMode && !!asset.exclusivePrice;

  /* ── Stepper handlers ───────────────────────────────────────────────────── */
  const handleDecrement = () => {
    setState((s) => ({ ...s, participants: Math.max(1, s.participants - 1) }));
  };

  const handleIncrement = () => {
    if (state.participants >= max) {
      toast.warning(`Solo ${max} posti disponibili`);
      return;
    }
    setState((s) => ({ ...s, participants: s.participants + 1 }));
  };

  /* ── Tier / exclusive handlers ──────────────────────────────────────────── */
  const handleTierSelect = (tierKey: string, persons: number) => {
    setState((s) => ({ ...s, tierKey, participants: persons }));
  };

  const handleExclusiveSelect = () => {
    const persons = asset.exclusiveMaxPersons ?? asset.maxCapacity ?? 1;
    setState((s) => ({ ...s, tierKey: 'exclusive', participants: persons }));
  };

  /* ── Render ─────────────────────────────────────────────────────────────── */
  return (
    <div data-cid="step-participants" className="flex flex-col gap-6 py-2">
      <p className="text-base font-bold text-slate-800">Quanti siete?</p>

      {!isTimedMode ? (
        /* ── Stepper (per_person / per_group / flat) ────────────────────── */
        <div data-cid="step-participants-stepper" className="flex flex-col gap-4">
          <div className="flex items-center gap-5">
            <button
              type="button"
              onClick={handleDecrement}
              disabled={state.participants <= 1}
              aria-label="Diminuisci partecipanti"
              className="w-11 h-11 rounded-full border border-slate-200 flex items-center justify-center text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
            >
              <Minus size={16} />
            </button>

            <div className="flex items-baseline gap-1.5 min-w-[4rem] justify-center">
              <span className="text-3xl font-bold text-slate-800 tabular-nums">
                {state.participants}
              </span>
              <span className="text-sm text-slate-400">/ {max}</span>
            </div>

            <button
              type="button"
              onClick={handleIncrement}
              disabled={state.participants >= max}
              aria-label="Aumenta partecipanti"
              className="w-11 h-11 rounded-full border border-slate-200 flex items-center justify-center text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
            >
              <Plus size={16} />
            </button>
          </div>

          {state.slotRemaining !== null && (
            <p className="text-sm text-slate-500">
              <span className="font-semibold text-sky-600">{state.slotRemaining}</span>{' '}
              posti disponibili
            </p>
          )}
        </div>
      ) : (
        /* ── Tiered / exclusive (radio cards) ──────────────────────────── */
        <div
          className="flex flex-col gap-2"
          role="radiogroup"
          aria-label="Seleziona fascia o tipologia"
        >
          {nonExclusiveTiers.map((tier) => {
            const key = tier.label ?? String(tier.persons);
            const clientPrice = applyMarkup(Number(tier.price), markupRate);
            const isSelected = state.tierKey === key;

            return (
              <button
                key={key}
                type="button"
                role="radio"
                aria-checked={isSelected}
                data-cid={`step-participants-tier-${key}`}
                onClick={() => handleTierSelect(key, tier.persons)}
                className={[
                  'flex items-center justify-between px-4 py-3 rounded-xl border text-left transition-all',
                  isSelected
                    ? 'bg-sky-600 border-sky-600 text-white shadow-sm'
                    : 'bg-white border-slate-200 text-slate-700 hover:border-sky-400 hover:bg-sky-50 cursor-pointer',
                ].join(' ')}
              >
                <span className="text-sm font-semibold">
                  {tier.label ?? `${tier.persons} persone`}
                </span>
                <span
                  className={`text-sm font-bold ${
                    isSelected ? 'text-white' : 'text-sky-700'
                  }`}
                >
                  €{clientPrice}
                </span>
              </button>
            );
          })}

          {hasExclusive && (
            <button
              type="button"
              role="radio"
              aria-checked={state.tierKey === 'exclusive'}
              data-cid="step-participants-tier-exclusive"
              onClick={handleExclusiveSelect}
              className={[
                'flex items-center justify-between px-4 py-3 rounded-xl border text-left transition-all',
                state.tierKey === 'exclusive'
                  ? 'bg-sky-600 border-sky-600 text-white shadow-sm'
                  : 'bg-white border-slate-200 text-slate-700 hover:border-sky-400 hover:bg-sky-50 cursor-pointer',
              ].join(' ')}
            >
              <div className="flex flex-col gap-0.5">
                <span className="text-sm font-semibold">
                  {asset.exclusiveLabel ?? 'Esclusiva — tutta la barca'}
                </span>
                <span
                  className={`text-xs ${
                    state.tierKey === 'exclusive' ? 'text-white/70' : 'text-slate-400'
                  }`}
                >
                  fino a {asset.exclusiveMaxPersons ?? asset.maxCapacity} persone
                </span>
              </div>
              <span
                className={`text-sm font-bold ${
                  state.tierKey === 'exclusive' ? 'text-white' : 'text-sky-700'
                }`}
              >
                €{applyMarkup(asset.exclusivePrice!, markupRate)}
              </span>
            </button>
          )}

          {nonExclusiveTiers.length === 0 && !hasExclusive && (
            <p className="text-sm text-slate-400 text-center py-4">
              Nessuna opzione disponibile.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
